/**
 * The one durable client store, and the only place IndexedDB is opened.
 *
 * [`device-cache.md`](../../../docs/product/device-cache.md) names two durable rows — the
 * gate-list snapshot and the *"refresh token and server-issued session binding"* — and puts
 * both in **origin-scoped IndexedDB**. They share a database on purpose: a schema mismatch,
 * a quota eviction, or an erasure has to reach all of it at once, and two databases means two
 * teardown paths that can disagree.
 *
 * **Everything about this module is written so that losing it is survivable.** Private-mode
 * browsers, iOS's script-writable-storage cap, and quota eviction all take the database away
 * without warning, and the specified behavior is not an error state: *"Authentication falls
 * back to OTP when secure session state is unavailable"* and derived snapshots *"discard …
 * and rebuild from the cloud."* So `openDeviceStore()` never rejects — it degrades to a
 * memory store whose `durable` flag is `false`, and the callers above treat that as "this
 * launch will end at the OTP screen", not as a crash.
 *
 * `KeyValueStore` exists as an interface rather than a concrete IDB helper for one reason
 * beyond that fallback: **the lifecycle rules are the risky part, not the I/O**, and a
 * memory implementation lets purge-on-revoke, account-switch partitioning, and
 * never-restore-green be tested without adding a `fake-indexeddb` dependency to a package
 * whose install the user runs by hand.
 */

import { TIMED_OUT, deadline } from '../auth/timeout'
import { releaseStoreReloadClaim } from '../pwa/recoveryMarker'
import { reportStoreOpenDegraded } from '../telemetry/sink'

/**
 * 🔸 **Assumption, not a decision.** How long the boot gate waits for `indexedDB.open()` before
 * giving up on durable storage. It is the only thing between a browser that never answers and a
 * launch that never finishes, so it is short: the cost of being wrong is one OTP, and the cost of
 * being patient is an app that shows "טוען…" forever.
 */
export const STORE_OPEN_TIMEOUT_MS = 5_000

/**
 * 🔸 **Assumption, not a decision — and a replacement for a previous one, which is why the
 * reasoning is here rather than in a changelog.** How long one transaction may take before it is
 * treated as lost.
 *
 * **It was 10 s, and D304 vetoed that on two findings rather than on taste:**
 *
 *  - **Its stated justification did not hold in the container.** The old docblock read *"longer
 *    than the open budget because a write can legitimately queue behind another tab's"* — **there
 *    are no other tabs in a Capacitor WebView.** That argument only ever covered the browser PWA.
 *  - **A long budget buys nothing for the iOS-suspension case it was aimed at.** {@link deadline}
 *    is `setTimeout`-based, and timers do not fire while a WebView is suspended: the suspension
 *    freezes the timer alongside the transaction and consumes almost none of the budget. The timer
 *    fires shortly after resume whether it was set to 2 s or to 10 s.
 *
 * 2 s cuts the wedged-store wait by 80 % on a path where a resident is watching a spinner, and is
 * far above any healthy single-row transaction we have seen.
 *
 * **It stays 🔸, and the device pass did NOT retire it — which is worth stating precisely, because
 * the pass looks like it should have.** D304's pass measured a healthy single-row `put` at
 * **1–2 ms** and an open at **5–7 ms** against real WebKit (`measurements.md`). That is ~1000×
 * headroom rather than the ~10–20× this docblock first claimed on reasoning alone — so the
 * reasoning was wrong about the magnitude even though it was right about the direction. But those
 * numbers came from the **iOS Simulator**, whose IndexedDB sits on the host Mac's SSD rather than
 * on an iPhone's NAND, so they bound nothing about the device this budget exists for. **A
 * simulator timing is not a device timing, and treating one as the other is how a 🔸 gets promoted
 * without evidence** — which is what `AGENTS.md` §5 forbids. {@link storeTimings} is the
 * instrument; it needs a pass on real hardware before `measurements.md` can carry a row that
 * settles this.
 *
 * **Narrowing this without {@link StoreTransactionTimeoutError} being routed away from the
 * recovery ladder is a change that destroys healthy databases more often** — see that class.
 */
export const STORE_TRANSACTION_TIMEOUT_MS = 2_000

/**
 * 🔸 **Assumption, not a decision.** The recovery ladder's own budget for its best-effort salvage
 * read of `outbox`, which is shorter than every other budget here for one reason: **a false
 * timeout on it costs nothing.** Proceeding without receipts is already the best-effort semantic
 * of that step, so the only thing this number trades is how long a resident waits on a spinner
 * during a recovery that is already failing.
 *
 * Without a budget of its own the salvage would inherit {@link STORE_TRANSACTION_TIMEOUT_MS} and a
 * wedged read would burn it in full, in front of a user who has already typed a correct code.
 */
export const STORE_SALVAGE_TIMEOUT_MS = 1_000

/**
 * A transaction that hit {@link STORE_TRANSACTION_TIMEOUT_MS} — **a class rather than a thrown
 * `Error` whose message is matched**, because two call sites now branch on it and
 * message-matching across a module boundary is the shape that silently stops working the day
 * someone rewords a string.
 *
 * **THE BRANCH IT FEEDS IS AN INTERLOCK, NOT A COPY IMPROVEMENT.** Before the recovery ladder
 * existed, a false timeout cost a rejection the caller already handled. After it, a false timeout
 * would **destroy a healthy database** — so narrowing the budget above and adding auto-destroy
 * pull directly against each other, and the resolution is that **a timeout must never reach the
 * ladder.** `auth/storeRecovery.ts` admits only structural `DOMException` rejections, and
 * `AuthProvider` renders this one as `serviceUnavailable` ("try again", which is honest for a
 * timeout) rather than as `storageUnavailable`.
 */
export class StoreTransactionTimeoutError extends Error {
  constructor(store: StoreName, mode: IDBTransactionMode) {
    super(`indexeddb-transaction-timeout: ${mode} ${store}`)
    this.name = 'StoreTransactionTimeoutError'
  }
}

/**
 * **The connection to WebKit's storage server is gone, and the database is FINE.**
 *
 * iOS kills the WKWebView's **Networking process** — the process that serves IndexedDB — while the
 * app sits backgrounded under memory pressure. The web content process survives, so the app looks
 * alive; `CapacitorHttp` goes through URLSession and never touches that process, so `/auth/refresh`
 * keeps succeeding; and every IndexedDB connection the document holds is dead.
 * `IDBDatabase::connectionToServerLost` fails every live transaction and fires `close`, and
 * afterwards `db.transaction()` throws `InvalidStateError`. **WebKit bug
 * [309386](https://bugs.webkit.org/show_bug.cgi?id=309386) is the half that makes this a class of
 * its own: the document keeps an `IDBConnectionProxy` pointing at the dead server, so a NEW
 * `indexedDB.open()` fails identically until the page is RELOADED.** Fixed in iOS 26.5; the device
 * that produced this was on 18.6.
 *
 * **WHY THIS IS A SEPARATE ERROR AND NOT A STRUCTURAL ONE — it is {@link
 * StoreTransactionTimeoutError}'s interlock, one step further out.** `InvalidStateError` was on
 * `storeRecovery.ts`'s structural list, so rung 1 called `destroy()` on the **most likely iOS
 * failure** — a database with nothing wrong with it. Worse, the salvage that is supposed to make
 * auto-destroy safe reads through **the same dead connection**, so it recovers nothing, and the
 * `outbox` receipts — the one store this file says must never be discarded — went with it. So a
 * lost connection must not reach the ladder any more than a timeout may.
 *
 * **Detected on two signals, because neither is sufficient.** WebKit fires `close` on the
 * `IDBDatabase` and PR #60094 relies on that event, but reporters on iOS 17.4–18.x — exactly the
 * range the affected device sits in — say it did **not** fire reliably before the fix. So the
 * failing transaction's **message** is matched too, and by message rather than by name alone:
 * `InvalidStateError` is equally what a handle **we** closed throws, and telling those two apart is
 * the whole of what decides whether `destroy()` is correct. The message is matched and **never
 * reported** — see {@link errorNameOf} for why the sink carries names only.
 */
export class StoreConnectionLostError extends Error {
  constructor(
    /** Which signal recognised it — the diagnostic that says whether `close` fires on this OS. */
    readonly signal: 'close-event' | 'error-shape',
    /** The rejection this re-labels. Its `name` is kept; see below. */
    cause: unknown,
  ) {
    super(`indexeddb-connection-lost: ${signal}`)
    /**
     * **`name` stays the UNDERLYING `DOMException`'s, and that is deliberate on two counts.**
     *
     * It keeps the sink honest: `CredentialStoreFailureEvent.errorName` is documented as *the first
     * failure's `DOMException.name`*, and a class that renamed itself would put a TypeScript
     * identifier in a field whose whole value is that it reports what the engine said.
     *
     * **And it is what makes `isStructuralStoreFailure`'s class check load-bearing rather than
     * decorative.** With a name of its own, that check could be deleted and the ladder would still
     * refuse this error — because the invented name is not on the structural list — so the guard
     * would pass against a build with the interlock removed. Keeping `InvalidStateError` here means
     * deleting the check puts the error straight back on the structural path, which is what
     * `storeRecovery.test.ts` demonstrates red.
     */
    this.name = errorNameOf(cause)
  }
}

/**
 * The message fragments WebKit uses when a request is refused because the connection to the storage
 * server is gone. **Two, not a family**, and both are WebKit's own strings: the first is what a
 * request carrying a dead `IDBConnectionProxy` rejects with — it literally names the remedy this
 * change implements — and the second is what `IDBDatabase::transaction` throws once `m_closePending`
 * is set, which `connectionToServerLost` sets.
 *
 * **The second is also what a handle we closed ourselves throws**, which is why {@link
 * looksLikeLostConnection} consults it only when this document did not do the closing. Widen this
 * list from a reported occurrence, never from reasoning: a fragment that also matches an ordinary
 * `InvalidStateError` turns the interlock into a hole and the symptom is a reload instead of a
 * recovery.
 */
const CONNECTION_LOST_FRAGMENTS: readonly string[] = [
  'connection to indexed database server lost',
  'database connection is closing',
]

/**
 * `true` when this document's storage server went away, as opposed to this document having closed
 * its own handle. `closedByUs` is the discriminator the message cannot supply.
 */
function looksLikeLostConnection(error: unknown, closedByUs: boolean): boolean {
  if (closedByUs) return false
  if (errorNameOf(error) !== 'InvalidStateError') return false
  const message = (error as { message?: unknown } | null | undefined)?.message
  if (typeof message !== 'string') return false
  const lowered = message.toLowerCase()
  return CONNECTION_LOST_FRAGMENTS.some((fragment) => lowered.includes(fragment))
}

/**
 * Bumped when a stored shape changes. A mismatch discards derived data — see `upgrade()`.
 *
 * **2 (D134):** `CachedGate.lastState?: string` became `lastPosition?: GatePosition`. The old field
 * was mistyped against the wire and never held a value, so nothing is lost by discarding — but the
 * snapshot store is versioned precisely so that judgement is not required at read time.
 *
 * **3 (P16):** the `outbox` store arrives — `device-cache.md`'s offline-BLE reconciliation receipt,
 * the one row P6 built every neighbour of and left with no live path. Bumping discards `snapshots`
 * as every bump does, which costs one `GET /gates`; **it does not touch `outbox`**, and that
 * asymmetry is the whole of the change (see `DERIVED_STORES`).
 */
export const SCHEMA_VERSION = 3

export const DB_NAME = 'selogate'

/**
 * `credentials` holds the refresh token + binding (survives an upgrade; losing it costs the
 * user an OTP). `snapshots` is **derived data** and is discarded on any version change.
 *
 * **`outbox` is P16's, and it is the only store in this file that is neither.** It holds
 * completed-BLE reconciliation receipts: records of gates that **already physically opened**
 * while the phone had no reception, which the server has never seen. `device-cache.md` calls it
 * *"an audit receipt, not a command queue"*, and everything below follows from that one sentence —
 * including that it must never carry a BLE password (see `web/src/ble/outbox.ts`).
 */
export type StoreName = 'credentials' | 'snapshots' | 'outbox'

const STORE_NAMES: readonly StoreName[] = ['credentials', 'snapshots', 'outbox']

/**
 * Stores that are rebuildable from the API, and are therefore dropped on a schema change.
 *
 * **`outbox` is deliberately absent, and this is the load-bearing line.** A snapshot is derived
 * data and re-fetching one costs a request; a receipt is *the only authoritative state the client
 * holds that the server has not seen* (D94's own arithmetic for why P16 is Critical), so dropping
 * one on a version bump destroys the audit record of an opening that really happened, with
 * nothing anywhere able to notice. `credentials` is excluded for a weaker version of the same
 * reason — losing it costs an SMS — and this is the stronger one.
 *
 * The cost is that a receipt's shape is now permanent: a future change to it must migrate rather
 * than discard. `web/src/ble/outbox.ts` carries its own row version for exactly that.
 */
const DERIVED_STORES: readonly StoreName[] = ['snapshots']

export interface KeyValueStore {
  get<T>(store: StoreName, key: string): Promise<T | undefined>
  put(store: StoreName, key: string, value: unknown): Promise<void>
  remove(store: StoreName, key: string): Promise<void>
  clear(store: StoreName): Promise<void>
  keys(store: StoreName): Promise<readonly string[]>
  /** Erasure and corruption recovery: the whole database goes, not one row at a time. */
  destroy(): Promise<void>
  /**
   * `false` when the browser gave us nothing durable. The caller must not treat a memory
   * store as a session that will survive a reload — it announces the OTP fallback.
   */
  readonly durable: boolean
}

export function createMemoryStore(durable = false): KeyValueStore {
  const data = new Map<StoreName, Map<string, unknown>>()
  const bucket = (store: StoreName): Map<string, unknown> => {
    const existing = data.get(store)
    if (existing !== undefined) return existing
    const created = new Map<string, unknown>()
    data.set(store, created)
    return created
  }
  return {
    durable,
    get: <T>(store: StoreName, key: string) =>
      // Structured-clone semantics, approximately: a caller must not be able to mutate what
      // is "in storage" by holding on to the object it read back.
      Promise.resolve(clone(bucket(store).get(key)) as T | undefined),
    put: (store, key, value) => {
      bucket(store).set(key, clone(value))
      return Promise.resolve()
    },
    remove: (store, key) => {
      bucket(store).delete(key)
      return Promise.resolve()
    },
    clear: (store) => {
      bucket(store).clear()
      return Promise.resolve()
    },
    keys: (store) => Promise.resolve([...bucket(store).keys()]),
    destroy: () => {
      data.clear()
      return Promise.resolve()
    },
  }
}

function clone(value: unknown): unknown {
  if (value === undefined || value === null || typeof value !== 'object') return value
  return JSON.parse(JSON.stringify(value)) as unknown
}

/**
 * **The instrument that retires {@link STORE_TRANSACTION_TIMEOUT_MS}'s 🔸, and it exists for that
 * one job.** `measurements.md` carries no IndexedDB transaction latency; every budget in this file
 * is therefore reasoned rather than read. This times each real IndexedDB operation so a device
 * pass produces a distribution, `measurements.md` gains its row with this named as the instrument,
 * and the next person changing a budget is arguing with data.
 *
 * **Bounded, in-memory, and carrying nothing about a person** — an operation name, a store name,
 * a duration, an outcome. It is not the telemetry sink and does not report anywhere by itself;
 * {@link storeTimings} is read by whoever is holding the device (`native/AGENTS.md`'s on-device
 * console) or folded into a diagnostic event by a caller that has one.
 *
 * The memory store is deliberately not timed: a `Map` write is not the delay nobody has read.
 */
const TIMING_CAPACITY = 200

export interface StoreTiming {
  readonly op: 'get' | 'put' | 'remove' | 'clear' | 'keys' | 'open' | 'destroy'
  readonly store: StoreName | '-'
  readonly ms: number
  readonly outcome: 'ok' | 'failed' | 'timed-out'
}

const timings: StoreTiming[] = []

function recordTiming(entry: StoreTiming): void {
  // A ring rather than a growing array: this runs on every read the app makes, and an
  // instrument that leaks is a defect dressed as diagnostics.
  if (timings.length >= TIMING_CAPACITY) timings.shift()
  timings.push(entry)
}

/** Every timed operation this document has run, oldest first. A copy — a caller cannot push. */
export function storeTimings(): readonly StoreTiming[] {
  return [...timings]
}

/** Test/teardown hook, and the device pass's reset between runs. */
export function clearStoreTimings(): void {
  timings.length = 0
}

/**
 * **Where the resume rung's one-shot reload claim is RELEASED, and the position is the whole loop
 * bound.** `pwa/recoveryMarker.ts` holds one claim per app process, so a document that has already
 * reloaded itself over a lost connection cannot do it again — and the only thing that may hand that
 * claim back is **evidence that storage works**, which is a real IndexedDB operation completing.
 *
 * Releasing it anywhere earlier — at boot, on a successful open — would arm a second reload on a
 * device where the reload did not help, which is the unbounded boot loop `AGENTS.md` §4.7 names as
 * materially worse than the defect being fixed. Releasing it on a *memory* store would do the same
 * thing while looking like a success.
 *
 * **So it is called from {@link run}'s completion and NOT from {@link timed}, and the difference is
 * two real paths rather than a nicety.** `timed` resolves for things that are not evidence that
 * storage works. `destroy()` is timed and `destroyThis` **resolves on `onblocked`** — a delete
 * another connection is holding up, with nothing written and nothing read. And `timed` wraps the
 * outer {@link via}, whose reissue path is `openDeviceStore().then(reissue)`; `openOrDegrade()`
 * never rejects, so that store can be the **memory** one, whose `put` resolves immediately — which
 * is the memory store reaching the release path by the one door the "it is untimed" argument does
 * not cover. `run` is reached only by a transaction on this durable handle firing `oncomplete`,
 * which is the evidence the bound is written in terms of; a reissue onto another durable handle
 * releases through *that* handle's `run`, which is equally correct.
 *
 * Once per document, and guarded for {@link degraded}'s reason — a synchronous throw out of the
 * bridge must not turn a successful read into a rejection.
 */
let reloadClaimReleased = false

function noteDurableSuccess(): void {
  if (reloadClaimReleased) return
  reloadClaimReleased = true
  try {
    void releaseStoreReloadClaim()
  } catch (error) {
    console.warn('store-reload-claim-release-failed', error)
  }
}

/**
 * `op`'s duration and outcome, recorded whichever way it settles. Rejections propagate unchanged —
 * an instrument that swallowed one would be changing the behaviour it exists to observe.
 */
async function timed<T>(op: StoreTiming['op'], store: StoreName | '-', work: () => Promise<T>): Promise<T> {
  const started = performance.now()
  try {
    const value = await work()
    recordTiming({ op, store, ms: performance.now() - started, outcome: 'ok' })
    return value
  } catch (error) {
    const outcome = error instanceof StoreTransactionTimeoutError ? 'timed-out' : 'failed'
    recordTiming({ op, store, ms: performance.now() - started, outcome })
    throw error
  }
}

let opening: Promise<KeyValueStore> | undefined

/**
 * The store this app runs against. Cached per document, so the boot sequence and the auth
 * provider share one connection rather than racing two `onupgradeneeded` handlers.
 */
export function openDeviceStore(): Promise<KeyValueStore> {
  opening ??= openOrDegrade()
  return opening
}

/**
 * **R1-3 — the boot gate cannot hang, and a wedged store degrades exactly as a rejected one does.**
 *
 * `openIndexedDbStore()` wires four events and every one of them is a *promise*, not a guarantee:
 * a browser in a low-storage or private-mode state can accept `factory.open()` and then fire
 * nothing at all. That case reached the `opening ??=` cache below, which then held a promise that
 * could never settle — so every one of the seven callers awaiting the store awaited it for the
 * life of the document, and the app sat on "טוען…" rather than on the OTP screen it was designed
 * to fall back to.
 *
 * The race lands the fires-nothing case **exactly where `onerror` and `onblocked` already land**.
 * `catch` is kept and unchanged: contrary to the review's own premise `onblocked` *is* handled,
 * the reject path works today, and a race written as a `catch` around everything would have
 * removed the working degrade while claiming to fix the missing one.
 *
 * The `opening ??=` cache stays. Single-flighting the open is what makes seven callers cheap;
 * what was wrong was that it could cache a promise that never settles, and after this it cannot.
 */
async function openOrDegrade(): Promise<KeyValueStore> {
  const budget = deadline(STORE_OPEN_TIMEOUT_MS)
  const started = performance.now()
  try {
    const opened = await budget.settle(openIndexedDbStore())
    // Timed even though this function cannot fail: `STORE_OPEN_TIMEOUT_MS` is flagged as the next
    // budget to look at — at 2 s / 5 s the open becomes the dominant launch stall, since `#doStart`
    // holds the app on "טוען…" until it resolves — and that is to be settled **with a
    // measurement, not a second guess**. This is the measurement.
    const ms = performance.now() - started
    if (opened !== TIMED_OUT) {
      recordTiming({ op: 'open', store: '-', ms, outcome: 'ok' })
      return opened
    }
    recordTiming({ op: 'open', store: '-', ms, outcome: 'timed-out' })
    return degraded({ outcome: 'timed-out', errorName: 'none', elapsedMs: ms })
  } catch (error) {
    const ms = performance.now() - started
    recordTiming({ op: 'open', store: '-', ms, outcome: 'failed' })
    return degraded({ outcome: 'failed', errorName: errorNameOf(error), elapsedMs: ms })
  } finally {
    budget.cancel()
  }
}

/**
 * **D306 — the degrade reports itself, once per document.** Until this, a launch that could not
 * open IndexedDB read no credentials and landed signed out in silence — no request, no audit row,
 * nothing — which from the server is exactly what a device that lost its credentials looks like
 * (`session-eviction-handoff.md` §2, case 3). This is the one case of the three that is cheap
 * to separate, and it is separated here rather than in `SessionManager` because the open is
 * single-flighted here: one degrade, one event, whoever asked first.
 *
 * **Once per document is a flag, not a property of the cache.** `destroy()` and
 * `reopenDeviceStore()` both clear `opening` and run this path again, so without the flag a
 * D304 ladder whose reopen also degrades would file a second event — a ladder step counted as a
 * boot, inflating the very rate this event exists to measure. `resetDeviceStoreForTests` clears it.
 *
 * **Guarded, because `openDeviceStore()` never rejects and this must not be the exception.** The
 * sink's promise never rejects, but a synchronous throw on the way in — a mocked module missing
 * the export, a bridge access that throws — would otherwise turn the R1-3 degrade into a rejection
 * at the boot gate, which is the hang R1-3 removed.
 */
let degradeReported = false

function degraded(event: Parameters<typeof reportStoreOpenDegraded>[0]): KeyValueStore {
  if (!degradeReported) {
    degradeReported = true
    try {
      void reportStoreOpenDegraded(event)
    } catch (error) {
      console.warn('store-open-degrade-report-failed', error)
    }
  }
  return createMemoryStore(false)
}

/**
 * A `DOMException`'s `name`, or the closest honest thing. **Never the message** — a rejected
 * request's message can carry what was being written, and `sink.ts` carries no identifier.
 * Lives here rather than in `auth/storeRecovery.ts` because the open path needs it too and
 * `storeRecovery` already imports this module; it re-exports for its own callers.
 */
export function errorNameOf(error: unknown): string {
  const name = (error as { name?: unknown } | null | undefined)?.name
  return typeof name === 'string' && name.length > 0 ? name : 'unknown'
}

/** Test/teardown hook: forget the cached connection so the next `open` re-opens. */
export function resetDeviceStoreForTests(): void {
  opening = undefined
  degradeReported = false
  reloadClaimReleased = false
}

/**
 * **Rung 2 of the recovery ladder: drop the cached connection and open a genuinely fresh handle.**
 *
 * The mechanism already existed twice in this file — `onversionchange` clears `opening`, and
 * {@link resetDeviceStoreForTests} is the same move — but neither was reachable as production
 * behaviour, and reaching for the test hook would have made a recovery path depend on a name that
 * says it is not one. Hence a third, plainly-named door rather than a widened test seam.
 *
 * **It is a second attempt, NOT a different mechanism — and the handoff that commissioned it said
 * otherwise, so the correction is recorded here rather than silently applied.** That document
 * argued rung 2 was distinct because "rung 1 reuses the handle path". It does not: `destroyThis`
 * sets `opening = undefined` **before** `deleteDatabase`, so a *successful* destroy already leaves
 * rung 1's `openDeviceStore()` opening a genuinely fresh connection. This function's body and rung
 * 1's post-destroy open are the same act.
 *
 * **What rung 2 is actually worth is the three ways rung 1 can fail**, and it is reachable only
 * from those: `destroy()` threw, so rung 1 never reached its reopen at all — the one case that
 * fits *"the handle is bad and the data was never the problem"*; the post-destroy open **degraded
 * to a memory store**, which the durability guard refuses, and a second open may not; or the
 * post-destroy write itself failed. It costs ~6 lines, needs no loop guard, and earns its place
 * mostly through the **diagnostic**: a `rung2` in the sink means one of those three specifically,
 * which is a much narrower fact than `rung1`.
 *
 * **It is deliberately not a page reload, and `storeRecovery.ts` explains why at length** — the
 * short version is that a reload at that moment costs the resident a fresh OTP and one of their
 * five D65/D88 cap slots, spent on our own recovery path.
 */
export function reopenDeviceStore(): Promise<KeyValueStore> {
  opening = undefined
  return openDeviceStore()
}

/** Only for the dev entry point and tests — installs a store without touching IndexedDB. */
export function useDeviceStoreForTests(store: KeyValueStore): void {
  opening = Promise.resolve(store)
}

function openIndexedDbStore(): Promise<KeyValueStore> {
  const factory = globalThis.indexedDB as IDBFactory | undefined
  if (factory === undefined) return Promise.reject(new Error('no-indexeddb'))

  return new Promise<KeyValueStore>((resolve, reject) => {
    const request = factory.open(DB_NAME, SCHEMA_VERSION)
    request.onupgradeneeded = () => upgrade(request.result)
    request.onblocked = () => reject(new Error('indexeddb-blocked'))
    request.onerror = () => reject(request.error ?? new Error('indexeddb-open-failed'))
    request.onsuccess = () => resolve(wrap(request.result))
  })
}

/**
 * *"If an upgrade is incompatible with locally saved data, discard the derived data and
 * rebuild it from the API"* — so a version change **deletes and recreates** every derived
 * store and leaves `credentials` alone. Discarding a snapshot costs one request; discarding
 * a session costs an SMS, and the two are not the same mistake.
 */
function upgrade(db: IDBDatabase): void {
  for (const name of DERIVED_STORES) {
    if (db.objectStoreNames.contains(name)) db.deleteObjectStore(name)
  }
  for (const name of STORE_NAMES) {
    if (!db.objectStoreNames.contains(name)) db.createObjectStore(name)
  }
}

function wrap(db: IDBDatabase): KeyValueStore {
  let closed = false
  /**
   * **This document closed the handle**, by `onversionchange` or by `destroyThis`. It is the
   * discriminator {@link looksLikeLostConnection} cannot get from the message: WebKit throws the
   * same `InvalidStateError` whether the connection was taken away or we gave it up, and only one
   * of those two means the database is healthy and the page needs reloading.
   */
  let selfClosed = false
  /**
   * **WebKit's `close` event — the storage server went away and every transaction on this handle is
   * already dead.** Per spec this fires on a *forced* close only, never on `db.close()`, so it is
   * the cleanest signal there is. It is not the only one: reporters on iOS 17.4–18.x say it did not
   * fire reliably before the 26.5 fix, which is the exact range that produced this defect — hence
   * the message match beside it. `opening` is dropped for `onversionchange`'s reason, one step
   * further: every later caller must open a fresh connection rather than re-issue on a dead one.
   */
  let connectionLost = false

  db.onclose = () => {
    connectionLost = true
    opening = undefined
  }

  /**
   * Re-label a rejection that is really a lost connection, so `storeRecovery.ts` can refuse it the
   * way it refuses a timeout. **A timeout passes through untouched** — it is its own interlock and
   * classing it as a second one would lose the `serviceUnavailable` copy that is honest for it.
   */
  const asFailure = (error: unknown): unknown => {
    if (error instanceof StoreTransactionTimeoutError) return error
    if (connectionLost) return new StoreConnectionLostError('close-event', error)
    if (looksLikeLostConnection(error, selfClosed)) return new StoreConnectionLostError('error-shape', error)
    return error
  }

  /**
   * **R1-4 — the next schema bump must not be a silent durability loss.**
   *
   * A connection held open across a version change is what makes the *other* tab's open fire
   * `onblocked`, which rejects, which degrades that tab to a memory store — a silent boot at the
   * OTP screen, with re-entry costing an SMS. Nothing installs this handler today, so a tab holds
   * `selogate` v2 for the life of its document; the defect is UNPROVEN and **deterministic on the
   * next `SCHEMA_VERSION` bump.**
   *
   * Closing is only half of it: after `db.close()` this handle serves nothing, and every caller
   * that already holds it (`SessionManager` caches one) would get `InvalidStateError` on its next
   * read. So the cached promise is dropped — the next `openDeviceStore()` opens a live connection
   * — and the methods below re-issue through that one instead of throwing.
   */
  db.onversionchange = () => {
    closed = true
    selfClosed = true
    db.close()
    opening = undefined
  }

  /**
   * Runs against this connection while it is live; once `onversionchange` has closed it, the
   * call is re-issued against whatever `openDeviceStore()` opens next. The IDB body cannot
   * simply be replayed — it is a callback over an `IDBObjectStore` — so each method supplies
   * both forms.
   */
  function via<T>(own: () => Promise<T>, reissue: (store: KeyValueStore) => Promise<T>): Promise<T> {
    return closed ? openDeviceStore().then(reissue) : own()
  }

  function run<T>(
    store: StoreName,
    mode: IDBTransactionMode,
    body: (os: IDBObjectStore) => IDBRequest,
  ): Promise<T> {
    const settledPromise = new Promise<T>((resolve, reject) => {
      const tx = db.transaction(store, mode)
      const request = body(tx.objectStore(store))
      let result: T
      let settled = false
      const fail = (error: DOMException | null, fallback: string) => {
        if (settled) return
        settled = true
        reject(error ?? new Error(fallback))
      }
      // Request success means only that IndexedDB accepted the operation into the transaction.
      // Durability begins at transaction completion; an abort may still follow this callback.
      request.onsuccess = () => {
        result = request.result as T
      }
      request.onerror = () => fail(request.error, 'indexeddb-request-failed')
      tx.onerror = () => fail(tx.error, 'indexeddb-transaction-failed')
      tx.onabort = () => fail(tx.error, 'indexeddb-transaction-aborted')
      tx.oncomplete = () => {
        if (settled) return
        settled = true
        resolve(result)
      }
    })

    // **R1-3(2).** The promise above settles from four events and from nothing else, and an iOS
    // tab suspended mid-transaction may fire none of them — the same dead air as a wedged open,
    // one layer down. It rejects rather than degrading: a read has no memory fallback that would
    // be honest, and every caller of this already handles a rejection (a failed credential write
    // is D128's fail-closed path, a failed snapshot write is best-effort).
    const budget = deadline(STORE_TRANSACTION_TIMEOUT_MS)
    return budget
      .settle(settledPromise)
      .then((value) => {
        if (value === TIMED_OUT) throw new StoreTransactionTimeoutError(store, mode)
        // A transaction on this connection reached `oncomplete`. That — and nothing weaker — is
        // the evidence D309's reload claim is handed back on. See {@link noteDurableSuccess}.
        noteDurableSuccess()
        return value
      })
      .catch((error: unknown) => {
        throw asFailure(error)
      })
      .finally(() => budget.cancel())
  }

  const destroyThis = (): Promise<void> =>
    new Promise<void>((resolve, reject) => {
      db.close()
      selfClosed = true
      opening = undefined
      const request = (globalThis.indexedDB as IDBFactory).deleteDatabase(DB_NAME)
      // A blocked delete still resolves: another tab holds the database open, and the
      // rows this call was asked to destroy are already unreachable from this one. The
      // caller has cleared its in-memory state either way.
      request.onblocked = () => resolve()
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error ?? new Error('indexeddb-delete-failed'))
    })

  // Every method is wrapped in `timed` at this level rather than inside `run`, so a call that
  // takes the `via` reissue path is measured end to end — that path re-opens a connection, which
  // is exactly the delay a caller feels and the one `run` alone cannot see.
  return {
    durable: true,
    get: <T>(store: StoreName, key: string) =>
      timed('get', store, () =>
        via<T | undefined>(
          () => run<T | undefined>(store, 'readonly', (os) => os.get(key)),
          (fresh) => fresh.get<T>(store, key),
        ),
      ),
    put: (store, key, value) =>
      timed('put', store, () =>
        via(
          () => run<void>(store, 'readwrite', (os) => os.put(value, key)),
          (fresh) => fresh.put(store, key, value),
        ),
      ),
    remove: (store, key) =>
      timed('remove', store, () =>
        via(
          () => run<void>(store, 'readwrite', (os) => os.delete(key)),
          (fresh) => fresh.remove(store, key),
        ),
      ),
    clear: (store) =>
      timed('clear', store, () =>
        via(
          () => run<void>(store, 'readwrite', (os) => os.clear()),
          (fresh) => fresh.clear(store),
        ),
      ),
    keys: (store) =>
      timed('keys', store, () =>
        via<readonly string[]>(
          async () => (await run<IDBValidKey[]>(store, 'readonly', (os) => os.getAllKeys())).map(String),
          (fresh) => fresh.keys(store),
        ),
      ),
    destroy: () => timed('destroy', '-', () => via(destroyThis, (fresh) => fresh.destroy())),
  }
}

/** Test-only deep import: exercise transaction event ordering without opening browser storage. */
export const wrapIndexedDbForTests = wrap
